import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, ShieldCheck } from 'lucide-react';

/**
 * BuildPilotVerdict, the top-of-page editorial summary strip.
 *
 * Use case:
 *   First block of any resource page. Reads as "BuildPilot's bottom-line
 *   verdict in one sentence before you read the facts". Sets the
 *   editorial tone immediately so the page never feels like an
 *   auto-generated reference page.
 *
 * Visual treatment:
 *   Full-width strip with a 3px brand-gradient top border, white
 *   background, brand-blue eyebrow + ShieldCheck glyph (matches the
 *   "Editor reviewed" feel without a face). One line, two lines max.
 *
 * Legal posture:
 *   Label vocabulary stays in the editorial register: "verdict",
 *   "reading", "view", "opinion". Never "advice" or "recommendation".
 *   For real one-to-one advice, the strip optionally surfaces a soft
 *   pointer to One on One Support (via `showAdvicePointer`).
 */
const BuildPilotVerdict = ({
  children,
  reviewedDate,
  showSourcesLink = true,
  showAdvicePointer = true,
  sourcesHref = '/sources',
  className = '',
  testId = 'buildpilot-verdict',
}) => {
  return (
    <aside
      data-testid={testId}
      className={`relative bg-white rounded-2xl px-5 py-5 sm:px-7 sm:py-6 my-6 sm:my-8 shadow-sm ${className}`}
      style={{
        border: '1px solid transparent',
        backgroundImage:
          'linear-gradient(white, white), linear-gradient(135deg, #0a2540 0%, #1d8efe 50%, #4abff2 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1d8efe]/10 flex items-center justify-center">
          <ShieldCheck className="w-4 h-4 text-[#1d8efe]" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-1.5"
            data-testid={`${testId}-eyebrow`}
          >
            BuildPilot verdict
          </p>
          <div className="text-[15px] sm:text-[16.5px] text-[#1d1d1f] leading-[1.55] font-medium">
            {typeof children === 'string' ? <p>{children}</p> : children}
          </div>

          {(showSourcesLink || showAdvicePointer || reviewedDate) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-[#5a5a5f]">
              {reviewedDate && (
                <span data-testid={`${testId}-reviewed`}>
                  Reviewed <time dateTime={reviewedDate}>{new Date(reviewedDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
                </span>
              )}
              {showSourcesLink && (
                <Link
                  to={sourcesHref}
                  className="inline-flex items-center gap-1 font-semibold text-[#1d8efe] hover:text-[#1670cc]"
                  data-testid={`${testId}-sources-link`}
                >
                  Read our sources <ArrowRight className="w-3 h-3" />
                </Link>
              )}
              {showAdvicePointer && (
                <Link
                  to="/one-on-one-support"
                  className="inline-flex items-center gap-1 font-semibold text-[#1d1d1f] hover:text-[#1d8efe]"
                  data-testid={`${testId}-advice-link`}
                >
                  Want real advice? See One on One Support <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

/**
 * BuildPilotWatchOut, the editorial "watch-out" callout.
 *
 * Use case:
 *   Highlight a specific commercial trap BuildPilot has seen Adelaide
 *   families fall into. Sits inline with body copy, mid-article.
 *
 * Visual treatment:
 *   Deep navy block with a cyan/white accent. Deliberately NOT amber
 *   or yellow (the user banned pastel/AI-slop colors). Reads as the
 *   serious, "we have seen this go wrong" moment.
 *
 * Legal posture:
 *   Same as BuildPilotOpinion. Never an instruction; always framed as
 *   "in our experience" / "the trap we see".
 */
export const BuildPilotWatchOut = ({
  children,
  label = 'BuildPilot watch-out',
  className = '',
  testId = 'buildpilot-watchout',
  showAdvicePointer = true,
}) => {
  return (
    <div
      data-testid={testId}
      className={`p-4 sm:p-5 rounded-lg shadow-md my-6 ${className}`}
      style={{
        background: 'linear-gradient(135deg, #0a2540 0%, #1d3a5f 100%)',
        color: '#ffffff',
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
          <AlertTriangle className="w-3.5 h-3.5 text-[#4abff2]" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[#4abff2] mb-2"
            data-testid={`${testId}-eyebrow`}
          >
            {label}
          </p>
          <div className="text-[13px] sm:text-[14px] text-white leading-relaxed [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-white/90">
            {typeof children === 'string' ? <p>{children}</p> : children}
          </div>
          {showAdvicePointer && (
            <Link
              to="/one-on-one-support"
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-white/90 hover:text-white uppercase tracking-[0.06em]"
              data-testid={`${testId}-advice-link`}
            >
              Get this checked on your build <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildPilotVerdict;
